'use client';

import { cn } from '@/utils/cn';
import { X } from 'lucide-react';
import React, { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { handleDomBubbleEvent } from '@/utils/dom';
import { MODAL_ROOT_ID, MODAL_ROOT_Z_INDEX } from '@/configs/dom.config';

interface IModalProps {
  isOpen?: boolean;
  children: React.ReactNode;
  onClose?: () => void;
  isCloseOutside?: boolean;
  onClickOutside?: () => void;
  isButtonClose?: boolean;
  className?: string;
  closeButtonClassName?: string;
  lockBodyScroll?: boolean;
  zIndex?: number;
}

const Modal = memo(
  ({
    isOpen,
    children,
    onClose,
    isCloseOutside = true,
    onClickOutside,
    isButtonClose = true,
    className,
    closeButtonClassName,
    lockBodyScroll = true,
    zIndex = MODAL_ROOT_Z_INDEX,
  }: IModalProps) => {
    // HOOKS
    const [modalRoot] = useState<HTMLElement | null>(() => {
      if (typeof window === 'undefined') return null;
      return document.getElementById(MODAL_ROOT_ID) as HTMLElement;
    });

    // Lock body scroll when modal is open
    useEffect(() => {
      if (!isOpen || !lockBodyScroll) return;

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyPaddingRight = document.body.style.paddingRight;
      const originalBodyPosition = document.body.style.position;
      const originalBodyTop = document.body.style.top;
      const originalBodyWidth = document.body.style.width;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      // Get current scroll position
      const scrollY = window.scrollY;

      // Prevent body scroll - use position fixed for more reliable locking
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';

      // Prevent layout shift from scrollbar
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      return () => {
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.paddingRight = originalBodyPaddingRight;
        document.body.style.position = originalBodyPosition;
        document.body.style.top = originalBodyTop;
        document.body.style.width = originalBodyWidth;
        document.documentElement.style.overflow = originalHtmlOverflow;

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }, [isOpen, lockBodyScroll]);

    // If modal root is not found, return null
    if (!modalRoot) return null;

    // RENDER
    return createPortal(
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            role="dialog"
            aria-modal="true"
            style={{
              zIndex,
            }}
            className="fixed inset-0 flex items-center justify-center bg-black/5 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                onClickOutside?.();
                if (isCloseOutside) {
                  onClose?.();
                }
              }
            }}
          >
            <motion.div
              tabIndex={-1}
              className={cn(
                'p-[24px] rounded-[16px] relative border border-[#D9D9D9] bg-[#FFF] mx-4 md:mx-0  w-full max-h-[90vh] flex flex-col',
                'focus:outline-none',
                className
              )}
              style={{
                boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {isButtonClose ? (
                <motion.button
                  type="button"
                  aria-label="Close modal"
                  onClick={(e) => {
                    handleDomBubbleEvent(e);
                    onClose?.();
                  }}
                  className={cn(
                    'absolute top-[16px] right-[16px] rounded-full p-[4px] flex items-center justify-center z-10 cursor-pointer group ',
                    'hover:bg-[#E5E5E5]  transition-colors duration-150',
                    'focus:outline-none',
                    closeButtonClassName
                  )}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  <X
                    className={cn(
                      'text-[#9CA3AF] w-[16px] h-[16px] shrink-0',
                      'group-hover:text-[#0C8DBF] transition-colors duration-150'
                    )}
                    aria-hidden="true"
                  />
                </motion.button>
              ) : null}

              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      modalRoot
    );
  }
);

Modal.displayName = 'Modal';
export default Modal;
